import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { PriorAuthSyncResponseT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { triggerPriorAuthSync } from '../api/priorAuthApi';
import { priorAuthKeys } from './keys';

export interface PriorAuthSyncVariables {
  /**
   * When present the mutation invalidates the patient's cached PA list on
   * success so the tab re-fetches and picks up any rows the poll/process
   * pipeline just touched.
   */
  patientUserId?: string;
}

/**
 * Forces an immediate ``poll_pending_prior_auths`` + ``process_all_dirty_rows``
 * pass on the backend — the manual equivalent of the two scheduled jobs that
 * normally keep PAs and CareState in sync.
 *
 * Scope is intentionally global; the backend does not filter by patient
 * because the poller walks our SQL mirror. We invalidate the list for the
 * *current* patient as a pragmatic choice — the tab only ever shows one
 * patient at a time, and other tabs' lists will refetch on their own next
 * mount.
 */
export function usePriorAuthSyncMutation() {
  const queryClient = useQueryClient();

  return useMutation<PriorAuthSyncResponseT, Error, PriorAuthSyncVariables | void>({
    mutationKey: ['priorAuth', 'sync'],
    mutationFn: triggerPriorAuthSync,
    onSuccess: async (_response, variables) => {
      if (variables && variables.patientUserId) {
        await queryClient.invalidateQueries({
          queryKey: priorAuthKeys.patientList(variables.patientUserId),
        });
      }
    },
  });
}
